import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectDestructuringEmpty from "@babel/runtime/helpers/objectDestructuringEmpty";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Redirect, useLocation, useHistory } from "react-router-dom";
import { Button, Loader } from "@components/atoms";
import { CheckoutProgressBar } from "@components/molecules";
import { CartSummary, PaymentGatewaysList, translateAdyenConfirmationError, adyenNotNegativeConfirmationStatusCodes } from "@components/organisms";
import { Checkout } from "@components/templates";
import { useCart, useCheckout } from "@saleor/sdk";
import { CHECKOUT_STEPS, CheckoutStep } from "@temp/core/config";
import { checkoutMessages } from "@temp/intl";
import { parseQueryString } from "@temp/core/utils";
import { CheckoutRouter } from "./CheckoutRouter";
import { CheckoutAddressSubpage, CheckoutPaymentSubpage, CheckoutReviewSubpage, CheckoutShippingSubpage } from "./subpages";

var prepareCartSummary = function prepareCartSummary(totalPrice, subtotalPrice, shippingTaxedPrice, promoTaxedPrice, items) {
  var products = items === null || items === void 0 ? void 0 : items.map(function (_ref) {
    var _variant$product, _variant$product$thum, _variant$product2, _variant$product2$thu, _variant$product3, _variant$product3$thu;

    var id = _ref.id,
        variant = _ref.variant,
        totalPrice = _ref.totalPrice,
        quantity = _ref.quantity;
    return {
      id: id || "",
      name: variant.product.name || "",
      price: {
        gross: {
          amount: (totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.gross.amount) || 0,
          currency: (totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.gross.currency) || ""
        },
        net: {
          amount: (totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.net.amount) || 0,
          currency: (totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.net.currency) || ""
        }
      },
      quantity: quantity,
      sku: variant.sku || "",
      thumbnail: {
        alt: ((_variant$product = variant.product) === null || _variant$product === void 0 ? void 0 : (_variant$product$thum = _variant$product.thumbnail) === null || _variant$product$thum === void 0 ? void 0 : _variant$product$thum.alt) || undefined,
        url: (_variant$product2 = variant.product) === null || _variant$product2 === void 0 ? void 0 : (_variant$product2$thu = _variant$product2.thumbnail) === null || _variant$product2$thu === void 0 ? void 0 : _variant$product2$thu.url,
        url2x: (_variant$product3 = variant.product) === null || _variant$product3 === void 0 ? void 0 : (_variant$product3$thu = _variant$product3.thumbnail2x) === null || _variant$product3$thu === void 0 ? void 0 : _variant$product3$thu.url
      }
    };
  });
  return React.createElement(CartSummary, {
    shipping: shippingTaxedPrice,
    subtotal: subtotalPrice,
    promoCode: promoTaxedPrice,
    total: totalPrice,
    products: products
  });
};

var getCheckoutProgress = function getCheckoutProgress(loaded, activeStepIndex, steps) {
  return loaded ? React.createElement(CheckoutProgressBar, {
    steps: steps,
    activeStep: activeStepIndex
  }) : null;
};

var getButton = function getButton(text, onClick) {
  if (text) {
    return React.createElement(Button, {
      testingContext: "checkoutPageNextStepButton",
      onClick: onClick,
      type: "submit"
    }, React.createElement("div", {
      style: {
        textShadow: "0 0 1px #000"
      }
    }, text));
  }

  return null;
};

var CheckoutPage = function CheckoutPage(_ref2) {
  var _checkout$shippingMet, _buttonText;

  _objectDestructuringEmpty(_ref2);

  var location = useLocation();
  var history = useHistory();
  var querystring = parseQueryString(location);

  var _useCart = useCart(),
      cartLoaded = _useCart.loaded,
      shippingPrice = _useCart.shippingPrice,
      discount = _useCart.discount,
      subtotalPrice = _useCart.subtotalPrice,
      totalPrice = _useCart.totalPrice,
      items = _useCart.items;

  var _useCheckout = useCheckout(),
      checkoutLoaded = _useCheckout.loaded,
      checkout = _useCheckout.checkout,
      payment = _useCheckout.payment,
      availablePaymentGateways = _useCheckout.availablePaymentGateways,
      createPayment = _useCheckout.createPayment,
      completeCheckout = _useCheckout.completeCheckout;

  var intl = useIntl();

  if (cartLoaded && (!items || !(items === null || items === void 0 ? void 0 : items.length))) {
    return React.createElement(Redirect, {
      to: "/cart/"
    });
  }

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      submitInProgress = _useState2[0],
      setSubmitInProgress = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      paymentConfirmation = _useState4[0],
      setPaymentConfirmation = _useState4[1];

  var _useState5 = useState(payment === null || payment === void 0 ? void 0 : payment.gateway),
      _useState6 = _slicedToArray(_useState5, 2),
      selectedPaymentGateway = _useState6[0],
      setSelectedPaymentGateway = _useState6[1];

  var _useState7 = useState(payment === null || payment === void 0 ? void 0 : payment.token),
      _useState8 = _slicedToArray(_useState7, 2),
      selectedPaymentGatewayToken = _useState8[0],
      setSelectedPaymentGatewayToken = _useState8[1];

  var _useState9 = useState([]),
      _useState10 = _slicedToArray(_useState9, 2),
      paymentGatewayErrors = _useState10[0],
      setPaymentGatewayErrors = _useState10[1];

  useEffect(function () {
    setSelectedPaymentGateway(payment === null || payment === void 0 ? void 0 : payment.gateway);
  }, [payment === null || payment === void 0 ? void 0 : payment.gateway]);
  useEffect(function () {
    setSelectedPaymentGatewayToken(payment === null || payment === void 0 ? void 0 : payment.token);
  }, [payment === null || payment === void 0 ? void 0 : payment.token]);
  var isShippingRequiredForProducts = items && items.some(function (_ref3) {
    var _variant$product4;

    var variant = _ref3.variant;
    return (_variant$product4 = variant.product) === null || _variant$product4 === void 0 ? void 0 : _variant$product4.productType.isShippingRequired;
  });
  var stepsWithViews = CHECKOUT_STEPS.filter(function (_ref4) {
    var withoutOwnView = _ref4.withoutOwnView;
    return !withoutOwnView;
  });
  var steps = isShippingRequiredForProducts ? stepsWithViews : stepsWithViews.filter(function (_ref5) {
    var onlyIfShippingRequired = _ref5.onlyIfShippingRequired;
    return !onlyIfShippingRequired;
  });

  var getActiveStepIndex = function getActiveStepIndex() {
    var matchingStepIndex = steps.findIndex(function (_ref6) {
      var link = _ref6.link;
      return link === location.pathname;
    });
    return matchingStepIndex !== -1 ? matchingStepIndex : steps.length - 1;
  };

  var getActiveStep = function getActiveStep() {
    return steps[getActiveStepIndex()];
  };

  var checkoutAddressSubpageRef = useRef(null);
  var checkoutShippingSubpageRef = useRef(null);
  var checkoutPaymentSubpageRef = useRef(null);
  var checkoutReviewSubpageRef = useRef(null);
  var checkoutGatewayFormId = "gateway-form";
  var checkoutGatewayFormRef = useRef(null);

  var handleNextStepClick = function handleNextStepClick() {
    var _checkoutAddressSubpa, _checkoutShippingSubp, _checkoutPaymentSubpa, _checkoutReviewSubpag;

    // Some magic above and below ensures that the activeStepIndex will always
    // be in 0-3 range

    /* eslint-disable default-case */
    switch (getActiveStep().index) {
      case 0:
        if ((_checkoutAddressSubpa = checkoutAddressSubpageRef.current) === null || _checkoutAddressSubpa === void 0 ? void 0 : _checkoutAddressSubpa.submitAddress) {
          var _checkoutAddressSubpa2;

          (_checkoutAddressSubpa2 = checkoutAddressSubpageRef.current) === null || _checkoutAddressSubpa2 === void 0 ? void 0 : _checkoutAddressSubpa2.submitAddress();
        }

        break;

      case 1:
        if ((_checkoutShippingSubp = checkoutShippingSubpageRef.current) === null || _checkoutShippingSubp === void 0 ? void 0 : _checkoutShippingSubp.submitShipping) {
          var _checkoutShippingSubp2;

          (_checkoutShippingSubp2 = checkoutShippingSubpageRef.current) === null || _checkoutShippingSubp2 === void 0 ? void 0 : _checkoutShippingSubp2.submitShipping();
        }

        break;

      case 2:
        if ((_checkoutPaymentSubpa = checkoutPaymentSubpageRef.current) === null || _checkoutPaymentSubpa === void 0 ? void 0 : _checkoutPaymentSubpa.submitPayment) {
          var _checkoutPaymentSubpa2;

          (_checkoutPaymentSubpa2 = checkoutPaymentSubpageRef.current) === null || _checkoutPaymentSubpa2 === void 0 ? void 0 : _checkoutPaymentSubpa2.submitPayment();
        }

        break;

      case 3:
        if ((_checkoutReviewSubpag = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag === void 0 ? void 0 : _checkoutReviewSubpag.complete) {
          var _checkoutReviewSubpag2;

          (_checkoutReviewSubpag2 = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag2 === void 0 ? void 0 : _checkoutReviewSubpag2.complete();
        }

        break;
    }
  };

  var handleStepSubmitSuccess = function handleStepSubmitSuccess(currentStep, data) {
    var activeStepIndex = getActiveStepIndex();

    if (currentStep === CheckoutStep.Review) {
      history.push({
        pathname: "/order-finalized",
        state: data
      });
    } else {
      history.push(steps[activeStepIndex + 1].link);
    }
  };

  var shippingTaxedPrice = (checkout === null || checkout === void 0 ? void 0 : (_checkout$shippingMet = checkout.shippingMethod) === null || _checkout$shippingMet === void 0 ? void 0 : _checkout$shippingMet.id) && shippingPrice ? {
    gross: shippingPrice,
    net: shippingPrice
  } : null;
  var promoTaxedPrice = discount && {
    gross: discount,
    net: discount
  };
  var checkoutView = cartLoaded && checkoutLoaded ? React.createElement(CheckoutRouter, {
    items: items,
    checkout: checkout,
    payment: payment,
    totalPrice: totalPrice,
    renderAddress: function renderAddress(props) {
      return React.createElement(CheckoutAddressSubpage, _extends({
        ref: checkoutAddressSubpageRef,
        changeSubmitProgress: setSubmitInProgress,
        onSubmitSuccess: function onSubmitSuccess() {
          return handleStepSubmitSuccess(CheckoutStep.Address);
        }
      }, props));
    },
    renderShipping: function renderShipping(props) {
      return React.createElement(CheckoutShippingSubpage, _extends({
        ref: checkoutShippingSubpageRef,
        changeSubmitProgress: setSubmitInProgress,
        onSubmitSuccess: function onSubmitSuccess() {
          return handleStepSubmitSuccess(CheckoutStep.Shipping);
        }
      }, props));
    },
    renderPayment: function renderPayment(props) {
      return React.createElement(CheckoutPaymentSubpage, _extends({
        ref: checkoutPaymentSubpageRef,
        paymentGatewayFormRef: checkoutGatewayFormRef,
        changeSubmitProgress: setSubmitInProgress,
        onSubmitSuccess: function onSubmitSuccess() {
          return handleStepSubmitSuccess(CheckoutStep.Payment);
        },
        onPaymentGatewayError: setPaymentGatewayErrors
      }, props));
    },
    renderReview: function renderReview(props) {
      return React.createElement(CheckoutReviewSubpage, _extends({
        ref: checkoutReviewSubpageRef,
        paymentGatewayFormRef: checkoutGatewayFormRef,
        selectedPaymentGatewayToken: selectedPaymentGatewayToken,
        changeSubmitProgress: setSubmitInProgress,
        onSubmitSuccess: function onSubmitSuccess(data) {
          return handleStepSubmitSuccess(CheckoutStep.Review, data);
        }
      }, props));
    }
  }) : React.createElement(Loader, null);

  var handleProcessPayment = /*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(gateway, token, cardData) {
      var _CHECKOUT_STEPS$find;

      var paymentConfirmStepLink, _yield$createPayment, dataError, errors;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              paymentConfirmStepLink = (_CHECKOUT_STEPS$find = CHECKOUT_STEPS.find(function (step) {
                return step.step === CheckoutStep.PaymentConfirm;
              })) === null || _CHECKOUT_STEPS$find === void 0 ? void 0 : _CHECKOUT_STEPS$find.link;
              _context.next = 3;
              return createPayment({
                gateway: gateway,
                token: token,
                creditCard: cardData,
                returnUrl: "".concat(window.location.origin).concat(paymentConfirmStepLink)
              });

            case 3:
              _yield$createPayment = _context.sent;
              dataError = _yield$createPayment.dataError;
              errors = dataError === null || dataError === void 0 ? void 0 : dataError.error;
              setSubmitInProgress(false);

              if (errors) {
                setPaymentGatewayErrors(errors);
              } else {
                setPaymentGatewayErrors([]);
                handleStepSubmitSuccess(CheckoutStep.Payment);
              }

            case 8:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function handleProcessPayment(_x, _x2, _x3) {
      return _ref7.apply(this, arguments);
    };
  }();

  var handleSubmitPayment = /*#__PURE__*/function () {
    var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(paymentData) {
      var _response$data, _response$data2, _response$data3, _response$dataError;

      var response;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return completeCheckout({
                paymentData: paymentData
              });

            case 2:
              response = _context2.sent;
              return _context2.abrupt("return", {
                confirmationData: (_response$data = response.data) === null || _response$data === void 0 ? void 0 : _response$data.confirmationData,
                confirmationNeeded: (_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.confirmationNeeded,
                order: (_response$data3 = response.data) === null || _response$data3 === void 0 ? void 0 : _response$data3.order,
                errors: (_response$dataError = response.dataError) === null || _response$dataError === void 0 ? void 0 : _response$dataError.error
              });

            case 4:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function handleSubmitPayment(_x4) {
      return _ref8.apply(this, arguments);
    };
  }();

  var handleSubmitPaymentSuccess = function handleSubmitPaymentSuccess(order) {
    setSubmitInProgress(false);
    setPaymentGatewayErrors([]);
    handleStepSubmitSuccess(CheckoutStep.Review, {
      id: order === null || order === void 0 ? void 0 : order.id,
      orderNumber: order === null || order === void 0 ? void 0 : order.number,
      token: order === null || order === void 0 ? void 0 : order.token
    });
  };

  var handlePaymentGatewayError = function handlePaymentGatewayError(errors) {
    var _steps$find;

    setSubmitInProgress(false);
    setPaymentGatewayErrors(errors);
    var paymentStepLink = (_steps$find = steps.find(function (step) {
      return step.step === CheckoutStep.Payment;
    })) === null || _steps$find === void 0 ? void 0 : _steps$find.link;

    if (paymentStepLink) {
      history.push(paymentStepLink);
    }
  };

  var paymentGatewaysView = availablePaymentGateways && React.createElement(PaymentGatewaysList, {
    paymentGateways: availablePaymentGateways,
    processPayment: handleProcessPayment,
    submitPayment: handleSubmitPayment,
    submitPaymentSuccess: handleSubmitPaymentSuccess,
    formId: checkoutGatewayFormId,
    formRef: checkoutGatewayFormRef,
    selectedPaymentGateway: selectedPaymentGateway,
    selectedPaymentGatewayToken: selectedPaymentGatewayToken,
    selectPaymentGateway: setSelectedPaymentGateway,
    onError: handlePaymentGatewayError,
    errors: paymentGatewayErrors
  });
  var activeStep = getActiveStep();
  var buttonText = activeStep.nextActionName;
  /* eslint-disable default-case */

  switch (activeStep.nextActionName) {
    case "Continue to Shipping":
      buttonText = intl.formatMessage(checkoutMessages.addressNextActionName);
      break;

    case "Continue to Payment":
      buttonText = intl.formatMessage(checkoutMessages.shippingNextActionName);
      break;

    case "Continue to Review":
      buttonText = intl.formatMessage(checkoutMessages.paymentNextActionName);
      break;

    case "Place order":
      buttonText = intl.formatMessage(checkoutMessages.reviewNextActionName);
      break;
  }

  useEffect(function () {
    var _CHECKOUT_STEPS$find2;

    var paymentConfirmStepLink = (_CHECKOUT_STEPS$find2 = CHECKOUT_STEPS.find(function (step) {
      return step.step === CheckoutStep.PaymentConfirm;
    })) === null || _CHECKOUT_STEPS$find2 === void 0 ? void 0 : _CHECKOUT_STEPS$find2.link;

    if (!submitInProgress && checkout && location.pathname === paymentConfirmStepLink && !paymentConfirmation) {
      handlePaymentConfirm();
    }
  }, [location.pathname, querystring, submitInProgress, checkout]);

  var handlePaymentConfirm = /*#__PURE__*/function () {
    var _ref9 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
      var _steps$find2, paymentStepLink, _yield$completeChecko, data, dataError, errors, _steps$find3, _paymentStepLink, _data$order, _data$order2, _data$order3, _steps$find4, _paymentStepLink2;

      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              /**
               * Prevent proceeding in confirmation flow in case of gateways that don't support it to prevent unknown bugs.
               */
              if ((payment === null || payment === void 0 ? void 0 : payment.gateway) !== "mirumee.payments.adyen") {
                paymentStepLink = (_steps$find2 = steps.find(function (step) {
                  return step.step === CheckoutStep.Payment;
                })) === null || _steps$find2 === void 0 ? void 0 : _steps$find2.link;

                if (paymentStepLink) {
                  history.push(paymentStepLink);
                }
              }

              setSubmitInProgress(true);
              setPaymentConfirmation(true);
              /**
               * Saleor API creates an order for not fully authorised payments, thus we accept all non negative payment result codes,
               * assuming the payment is completed, what means we can proceed further.
               * https://docs.adyen.com/checkout/drop-in-web?tab=http_get_1#step-6-present-payment-result
               */

              if (!adyenNotNegativeConfirmationStatusCodes.includes(querystring.resultCode)) {
                _context3.next = 14;
                break;
              }

              _context3.next = 6;
              return completeCheckout();

            case 6:
              _yield$completeChecko = _context3.sent;
              data = _yield$completeChecko.data;
              dataError = _yield$completeChecko.dataError;
              errors = dataError === null || dataError === void 0 ? void 0 : dataError.error;
              setSubmitInProgress(false);

              if (errors) {
                setPaymentGatewayErrors(errors);
                _paymentStepLink = (_steps$find3 = steps.find(function (step) {
                  return step.step === CheckoutStep.Payment;
                })) === null || _steps$find3 === void 0 ? void 0 : _steps$find3.link;

                if (_paymentStepLink) {
                  history.push(_paymentStepLink);
                }
              } else {
                setPaymentGatewayErrors([]);
                handleStepSubmitSuccess(CheckoutStep.Review, {
                  id: data === null || data === void 0 ? void 0 : (_data$order = data.order) === null || _data$order === void 0 ? void 0 : _data$order.id,
                  orderNumber: data === null || data === void 0 ? void 0 : (_data$order2 = data.order) === null || _data$order2 === void 0 ? void 0 : _data$order2.number,
                  token: data === null || data === void 0 ? void 0 : (_data$order3 = data.order) === null || _data$order3 === void 0 ? void 0 : _data$order3.token
                });
              }

              _context3.next = 17;
              break;

            case 14:
              setPaymentGatewayErrors([{
                message: translateAdyenConfirmationError(querystring.resultCode, intl)
              }]);
              _paymentStepLink2 = (_steps$find4 = steps.find(function (step) {
                return step.step === CheckoutStep.Payment;
              })) === null || _steps$find4 === void 0 ? void 0 : _steps$find4.link;

              if (_paymentStepLink2) {
                history.push(_paymentStepLink2);
                setSubmitInProgress(false);
                setPaymentConfirmation(false);
              }

            case 17:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    return function handlePaymentConfirm() {
      return _ref9.apply(this, arguments);
    };
  }();

  var activeStepIndex = getActiveStepIndex();
  return React.createElement(Checkout, {
    loading: submitInProgress,
    navigation: getCheckoutProgress(cartLoaded && checkoutLoaded, activeStepIndex, steps),
    cartSummary: prepareCartSummary(totalPrice, subtotalPrice, shippingTaxedPrice, promoTaxedPrice, items),
    checkout: checkoutView,
    paymentGateways: paymentGatewaysView,
    hidePaymentGateways: steps[activeStepIndex].step !== CheckoutStep.Payment,
    button: getButton((_buttonText = buttonText) === null || _buttonText === void 0 ? void 0 : _buttonText.toUpperCase(), handleNextStepClick)
  });
};

export { CheckoutPage };